<template>
  <div>
    <a-alert
      type="info"
      show-icon
      class="mb-3"
      :message="`${$t('decaissement.totalToPay')}: ${totalToPay.toFixed(3)}`"
    />

    <div v-for="(item, index) in schedules" :key="index" class="row mb-2">
      <div class="col-md-5">
        <a-date-picker
          style="width: 100%"
          :value="item.dueDate ? moment(item.dueDate) : null"
          format="DD/MM/YYYY"
          @change="(v) => updateDueDate(index, v)"
        />
      </div>
      <div class="col-md-5">
        <a-input-number
          :value="item.amount"
          :min="0"
          :step="0.001"
          style="width: 100%"
          @change="(v) => updateAmount(index, v)"
        />
      </div>
      <div class="col-md-2">
        <a-button type="danger" icon="delete" @click="removeSchedule(index)" />
      </div>
    </div>

    <a-button type="dashed" icon="plus" class="mb-3" @click="addSchedule">
      {{ $t("decaissement.addSchedule") }}
    </a-button>

    <div class="validation-bar mb-3" :class="{ match: sumMatch, mismatch: !sumMatch }">
      <span>
        {{ $t("dette.installments") }}: {{ schedulesSum.toFixed(3) }}
        / {{ $t("decaissement.totalToPay") }}: {{ totalToPay.toFixed(3) }}
      </span>
      <a-tag v-if="sumMatch" color="green">OK</a-tag>
      <a-tag v-else color="red">{{ $t("dette.sumMismatch") }}</a-tag>
    </div>

    <a-form-item :label="$t('comptabilite.description')">
      <a-textarea v-model="description" :rows="2" />
    </a-form-item>

    <a-button
      v-if="!hideSubmit"
      type="primary"
      :loading="loading"
      :disabled="!sumMatch"
      @click="submit"
    >
      {{ $t("action.enregistrer") }}
    </a-button>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "SchedulePaymentForm",
  props: {
    depense: { type: Object, default: null },
    /** When creating a new depense, pass the planned total (and beneficiaryType) instead of depense. */
    totalAmount: { type: Number, default: null },
    beneficiaryType: { type: String, default: null },
    loading: { type: Boolean, default: false },
    /** Hide the save button when embedded in the depense create form (parent submits all). */
    hideSubmit: { type: Boolean, default: false },
    /** When true, rows after the first get due dates = first date + N months. */
    autoAdvanceMonthlyFromFirst: { type: Boolean, default: false },
  },
  data() {
    return {
      schedules: [{ dueDate: new Date(), amount: null }],
      description: "",
    };
  },
  computed: {
    totalToPay() {
      if (this.totalAmount != null && this.totalAmount > 0) {
        return this.totalAmount;
      }
      return this.depense?.amount || this.depense?.amountTTC || this.depense?.money || 0;
    },
    schedulesSum() {
      return this.schedules.reduce((s, i) => s + (Number(i.amount) || 0), 0);
    },
    sumMatch() {
      return Math.abs(this.schedulesSum - this.totalToPay) < 0.01;
    },
  },
  methods: {
    moment,
    datePlusMonths(baseDate, monthOffset) {
      const base = moment(baseDate);
      if (!base.isValid()) return null;
      return base.clone().add(monthOffset, "months").toDate();
    },
    recalcMonthlyDatesFromFirst() {
      if (!this.autoAdvanceMonthlyFromFirst || !this.schedules.length) return;
      const firstDate = this.schedules[0].dueDate;
      if (!firstDate) return;
      for (let i = 1; i < this.schedules.length; i++) {
        this.schedules[i].dueDate = this.datePlusMonths(firstDate, i);
      }
    },
    addSchedule() {
      const newIndex = this.schedules.length;
      const newRow = { dueDate: new Date(), amount: null };
      if (this.autoAdvanceMonthlyFromFirst && this.schedules[0]?.dueDate) {
        newRow.dueDate = this.datePlusMonths(this.schedules[0].dueDate, newIndex);
      }
      this.schedules.push(newRow);
    },
    removeSchedule(index) {
      this.schedules.splice(index, 1);
      this.recalcMonthlyDatesFromFirst();
    },
    updateDueDate(index, value) {
      this.schedules[index].dueDate = value ? value.toDate() : null;
      if (this.autoAdvanceMonthlyFromFirst && index === 0) {
        this.recalcMonthlyDatesFromFirst();
      }
    },
    updateAmount(index, value) {
      this.schedules[index].amount = value;
    },
    submit() {
      const payload = this.buildPayload();
      if (!payload) return;
      this.$emit("submit", payload);
    },
    /** Returns null if invalid (caller shows messages). */
    buildPayload() {
      for (let i = 0; i < this.schedules.length; i += 1) {
        const row = this.schedules[i];
        if (!row.dueDate) {
          this.$message.error(
            this.$t("decaissement.validation.scheduleDateRequired", {
              n: i + 1,
            })
          );
          return null;
        }
        if (!Number(row.amount) || Number(row.amount) <= 0) {
          this.$message.error(
            this.$t("decaissement.validation.scheduleAmountRequired", {
              n: i + 1,
            })
          );
          return null;
        }
      }
      if (!this.sumMatch) {
        this.$message.error(this.$t("decaissement.schedulesMustMatchInvoice"));
        return null;
      }
      return {
        paymentMode: "scheduled",
        totalToPay: this.totalToPay,
        schedules: this.schedules,
        description: this.description,
      };
    },
    reset() {
      this.schedules = [{ dueDate: new Date(), amount: null }];
      this.description = "";
    },
  },
};
</script>

<style scoped>
.validation-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #f0f0f0;
}
.validation-bar.match {
  background: #f6ffed;
  border-color: #b7eb8f;
}
.validation-bar.mismatch {
  background: #fff2f0;
  border-color: #ffccc7;
}
</style>
